import React from 'react';
import Slider from 'react-slick';
import { getImageUrl } from '../utils/helpers';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Custom Arrow Components
const NextArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-gray-800 w-12 h-12 md:w-16 md:h-16 rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 group backdrop-blur-sm"
    aria-label="Next slide"
  >
    <svg className="w-6 h-6 md:w-8 md:h-8 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
    </svg>
  </button>
);

const PrevArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-gray-800 w-12 h-12 md:w-16 md:h-16 rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 group backdrop-blur-sm"
    aria-label="Previous slide"
  >
    <svg className="w-6 h-6 md:w-8 md:h-8 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
    </svg>
  </button>
);

const Carousel = ({ items }) => {
  const settings = {
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    fade: true,
    cssEase: 'cubic-bezier(0.87, 0, 0.13, 1)',
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          fade: false,
          nextArrow: <NextArrow />,
          prevArrow: <PrevArrow />,
        }
      }
    ]
  };

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className="w-full relative carousel-container shadow-xl">
      <Slider {...settings}>
        {items.map((item, index) => (
          <div key={item.id || index} className="relative outline-none">
            <div className="relative h-72 sm:h-96 md:h-[450px] lg:h-[500px] xl:h-[550px] bg-gray-100 overflow-hidden">
              {/* Image fills complete carousel area */}
              <img 
                src={getImageUrl(item.images)} 
                alt={item.title || `Slide ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  console.error('Image load error:', item);
                  e.target.src = `${process.env.REACT_APP_BASE_URL}/assets/images/placeholder.jpg`;
                }}
              />
              
              {/* Gradient Overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none"></div>
              
              {/* Content Overlay */}
              {item.title && (
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8 lg:p-10">
                  <div className="container">
                    <div className="max-w-4xl">
                      <h2 className="text-white text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold drop-shadow-2xl mb-2 sm:mb-3 animate-fade-in-up line-clamp-2">
                        {item.title}
                      </h2>
                      {item.description && (
                        <p className="text-white/90 text-sm sm:text-base md:text-lg drop-shadow-lg max-w-2xl animate-fade-in-up-delay line-clamp-2">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </Slider>

      <style jsx>{`
        .carousel-container {
          position: relative;
        }

        /* Hide default arrows */
        .carousel-container :global(.slick-prev),
        .carousel-container :global(.slick-next) {
          width: auto;
          height: auto;
        }
        
        .carousel-container :global(.slick-prev:before),
        .carousel-container :global(.slick-next:before) {
          display: none;
        }

        /* Custom Dots Styling */
        .carousel-container :global(.slick-dots) {
          bottom: 30px;
          z-index: 15;
        }

        .carousel-container :global(.slick-dots li) {
          margin: 0 8px;
        }

        .carousel-container :global(.slick-dots li button) {
          width: 12px;
          height: 12px;
          padding: 0;
        }

        .carousel-container :global(.slick-dots li button:before) {
          content: '';
          width: 12px;
          height: 12px;
          border: 2px solid white;
          border-radius: 50%;
          opacity: 0.6;
          background: transparent;
          transition: all 0.3s ease;
        }

        .carousel-container :global(.slick-dots li.slick-active button:before) {
          opacity: 1;
          background: white;
          transform: scale(1.2);
        }

        .carousel-container :global(.slick-dots li:hover button:before) {
          opacity: 0.9;
          transform: scale(1.1);
        }

        /* Smooth slide transition */
        .carousel-container :global(.slick-slide) {
          transition: opacity 0.3s ease-in-out;
        }

        /* Animations */
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .animate-fade-in-up-delay {
          animation: fade-in-up 0.8s ease-out 0.2s forwards;
          opacity: 0;
        }

        /* Mobile optimizations */
        @media (max-width: 768px) {
          .carousel-container :global(.slick-dots) {
            bottom: 20px;
          }
          
          .carousel-container :global(.slick-dots li button:before) {
            width: 10px;
            height: 10px;
          }
        }

        /* Ensure images fit properly */
        .carousel-container img {
          display: block;
          max-width: 100%;
          height: 100%;
        }

        /* Line clamp for text overflow */
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default Carousel;
